/**
 * BRAND SIMILAR NAMES PANEL
 *
 * Surfaces polishCatalog entries that live under a brand name similar
 * to yours ("Moon Cat" vs "Mooncat", "Mooncat!!!" vs "Mooncat"). Brand
 * owners can:
 *   - Claim & normalize — reuses `renameBrand` service to rewrite user
 *     items from the similar name to the brand's canonical form. The
 *     polishCatalog trigger re-aggregates automatically.
 *   - Dismiss — flags the variant as "not ours" so it stops appearing
 *     in this list. Stored on the brand doc's dismissedBrandNames.
 *
 * A collapsible "Previously dismissed" section lets the brand owner
 * re-surface a dismissed variant (in case of a mistake or a change of
 * mind) via an Un-dismiss action.
 */

import { useState, useEffect } from 'react';
import type { Brand } from '../../types/brand';
import {
  findSimilarBrandNames,
  dismissSimilarBrand,
  undismissSimilarBrand,
  getDismissedBrandNames,
  type SimilarBrandMatch,
} from '../../services/similarBrandNames';
import { renameBrand } from '../../services/brandRename';
import { Search, CheckCheck, X, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';

interface Props {
  brand: Brand;
}

export default function BrandSimilarNamesPanel({ brand }: Props) {
  const [matches, setMatches] = useState<SimilarBrandMatch[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyBrand, setBusyBrand] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [feedback, setFeedback] = useState<{ brand: string; text: string; kind: 'ok' | 'err' } | null>(null);

  const load = async () => {
    if (!brand?.id || !brand?.name) return;
    setLoading(true);
    try {
      const dismissedList = await getDismissedBrandNames(brand.id);
      setDismissed(dismissedList);
      const results = await findSimilarBrandNames(brand.name, dismissedList);
      setMatches(results);
    } catch (err) {
      console.error('findSimilarBrandNames failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id, brand?.name]);

  const claim = async (match: SimilarBrandMatch) => {
    if (!window.confirm(
      `Claim "${match.brand}" as yours?\n\nThis renames ${match.polishCount} polish${match.polishCount === 1 ? '' : 'es'} from "${match.brand}" to "${brand.name}" on every collector's items. Not reversible via the UI. Confirm you're sure these are your polishes.`
    )) return;
    setBusyBrand(match.brand);
    setFeedback(null);
    try {
      const summary = await renameBrand({
        brandId: brand.id,
        fromBrandLower: match.brand.toLowerCase(),
        toBrand: brand.name,
      });
      setFeedback({
        brand: match.brand,
        text: `Claimed. ${summary.itemsUpdated} item${summary.itemsUpdated === 1 ? '' : 's'} normalized to "${brand.name}".`,
        kind: 'ok',
      });
      await load();
    } catch (err) {
      setFeedback({ brand: match.brand, text: `Claim failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setBusyBrand(null);
    }
  };

  const dismiss = async (match: SimilarBrandMatch) => {
    setBusyBrand(match.brand);
    setFeedback(null);
    try {
      await dismissSimilarBrand(brand.id, match.brand);
      setFeedback({ brand: match.brand, text: 'Dismissed. Will not show up as a suggestion anymore.', kind: 'ok' });
      await load();
    } catch (err) {
      setFeedback({ brand: match.brand, text: `Dismiss failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setBusyBrand(null);
    }
  };

  const undismiss = async (brandName: string) => {
    setBusyBrand(brandName);
    setFeedback(null);
    try {
      await undismissSimilarBrand(brand.id, brandName);
      setFeedback({ brand: brandName, text: 'Restored to the suggestion list.', kind: 'ok' });
      await load();
    } catch (err) {
      setFeedback({ brand: brandName, text: `Un-dismiss failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setBusyBrand(null);
    }
  };

  const activeMatches = matches.filter((m) => !m.isDismissed);
  const dismissedMatches = matches.filter((m) => m.isDismissed);
  // Also include any dismissed brand names that no longer have any
  // polishCatalog docs (e.g. an old dismiss that has since been cleaned
  // up by admins) so the brand owner can still un-dismiss them.
  const dismissedOnlyNames = dismissed
    .filter((n) => !matches.some((m) => m.brand.toLowerCase() === n.toLowerCase()));

  return (
    <div className="glass-card p-6">
      <div className="flex items-center gap-2 mb-1">
        <Search className="w-4 h-4 text-glass-secondary" />
        <h2 className="text-lg font-semibold text-glass font-body">Similar brand names</h2>
      </div>
      <p className="text-sm text-glass-secondary font-body mb-4">
        Polishes attributed to brand names that look like yours ("Moon Cat" vs "Mooncat", typos, or extra punctuation). Claim to normalize them onto your brand, or dismiss if they're not yours.
      </p>

      {loading ? (
        <div className="text-sm text-glass-secondary font-body">Scanning polishCatalog…</div>
      ) : activeMatches.length === 0 ? (
        <div className="text-sm text-glass-secondary font-body p-3 bg-slate-800/40 border border-slate-600 rounded">
          No similar names found. If you find any manually, they should
          be normalized via the admin path.
        </div>
      ) : (
        <div className="space-y-2">
          {activeMatches.map((match) => {
            const isBusy = busyBrand === match.brand;
            const rowFeedback = feedback?.brand === match.brand ? feedback : null;
            return (
              <div
                key={match.brandSlug}
                className="bg-slate-800/40 border border-slate-600 rounded p-3 space-y-2"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-glass font-medium font-body">
                      {match.brand}
                    </div>
                    <div className="text-xs text-glass-muted font-body mt-0.5">
                      {match.polishCount} polish{match.polishCount === 1 ? '' : 'es'} · slug: <code>{match.brandSlug}</code>
                    </div>
                    {match.samplePolishes.length > 0 && (
                      <div className="text-xs text-glass-secondary font-body mt-1">
                        {match.samplePolishes.join(', ')}
                        {match.polishCount > match.samplePolishes.length && (
                          <span className="text-glass-muted"> +{match.polishCount - match.samplePolishes.length} more</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => claim(match)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 font-body"
                      title={`Rename "${match.brand}" to "${brand.name}" everywhere`}
                    >
                      <CheckCheck className="w-3 h-3" /> Claim
                    </button>
                    <button
                      onClick={() => dismiss(match)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                      title="Mark as not yours"
                    >
                      <X className="w-3 h-3" /> Dismiss
                    </button>
                  </div>
                </div>
                {rowFeedback && (
                  <div
                    className={`text-xs font-body px-2 py-1 rounded ${
                      rowFeedback.kind === 'ok'
                        ? 'bg-emerald-500/10 text-emerald-300'
                        : 'bg-red-500/10 text-red-300'
                    }`}
                  >
                    {rowFeedback.text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(dismissedMatches.length > 0 || dismissedOnlyNames.length > 0) && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowDismissed((v) => !v)}
            className="flex items-center gap-2 text-sm text-glass-secondary hover:text-glass font-body"
          >
            {showDismissed ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            Previously dismissed ({dismissedMatches.length + dismissedOnlyNames.length})
          </button>
          {showDismissed && (
            <div className="mt-2 space-y-2">
              {dismissedMatches.map((match) => {
                const isBusy = busyBrand === match.brand;
                const rowFeedback = feedback?.brand === match.brand ? feedback : null;
                return (
                  <div
                    key={match.brandSlug}
                    className="bg-slate-800/40 border border-slate-600 rounded p-3 space-y-2 opacity-70"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-glass font-medium font-body">
                          {match.brand}
                        </div>
                        <div className="text-xs text-glass-muted font-body mt-0.5">
                          {match.polishCount} polish{match.polishCount === 1 ? '' : 'es'} · dismissed
                        </div>
                      </div>
                      <button
                        onClick={() => undismiss(match.brand)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                      >
                        <RotateCcw className="w-3 h-3" /> Un-dismiss
                      </button>
                    </div>
                    {rowFeedback && (
                      <div
                        className={`text-xs font-body px-2 py-1 rounded ${
                          rowFeedback.kind === 'ok'
                            ? 'bg-emerald-500/10 text-emerald-300'
                            : 'bg-red-500/10 text-red-300'
                        }`}
                      >
                        {rowFeedback.text}
                      </div>
                    )}
                  </div>
                );
              })}
              {dismissedOnlyNames.map((n) => (
                <div
                  key={n}
                  className="bg-slate-800/40 border border-slate-600 rounded p-3 opacity-70 flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-glass font-medium font-body">{n}</div>
                    <div className="text-xs text-glass-muted font-body mt-0.5">
                      No polishes currently under this name. Dismiss retained in case it reappears.
                    </div>
                  </div>
                  <button
                    onClick={() => undismiss(n)}
                    disabled={busyBrand === n}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                  >
                    <RotateCcw className="w-3 h-3" /> Un-dismiss
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
